import { faShieldCheck } from "@fortawesome/pro-solid-svg-icons/faShieldCheck";
import { faShieldQuartered } from "@fortawesome/pro-solid-svg-icons/faShieldQuartered";
import { faThumbsUp } from "@fortawesome/pro-solid-svg-icons/faThumbsUp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Grid, IconButton, Typography, useTheme } from "@mui/material";
import { APP_PAGE_SIZE_SHORT } from "app/appConstants";
import { MprcClickable, MprcContainer, MprcLink, MprcRequestErrorReport, MprcSpinnerIndicator, MprcText } from "components";
import { UserItemDtoPaginatedList, UserPanelProfessionaInterestDto, UserPanelProfessionaInterestDtoPaginatedList } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { ListUsersDrawer } from "views/foundation/user/drawers/ListUsersDrawer";

interface IProps {
    title: string;
    userId: string;
    viewData: UserPanelProfessionaInterestDtoPaginatedList;
    onChange: (item: UserPanelProfessionaInterestDto, index: number) => void;
    onGetData: (pageSize: number) => Promise<boolean>;
}

/**
 * Painel lateral de interesses profissionais do usuário/autor
 * @param props
 */
export const PanelUserProfessionalInterest: React.FC<IProps> = (props) => {
    const { userId, title, viewData, onChange, onGetData } = props;

    const theme = useTheme();
    const api = useCoreApi();
    const session = useSession();
    const { t } = useTranslation("knowledge");
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const [posting, setPosting] = useState<boolean>(false);
    const [dialog, setDialog] = useState<any>();
    const [viewError, setViewError] = useState<any>();
    const [loading, setLoading] = useState<boolean>(false);
    const [showAll, setShowAll] = useState<boolean>(false);

    ///
    const handleAddEndorsement = (item: UserPanelProfessionaInterestDto, index: number) => {
        setPosting(true);
        api.v1.userManagement.addTopicEndorment({ topicId: item.topicId, userId })
            .then(result => {
                if (result.data === true) {
                    enqueueSnackbar(t("Feed.Panels.UserProfessionalInterest.AddEndorsementSuccessMessage"), { variant: "success" });
                    onChange({ ...item, isEndorsed: true, endorsementCount: item.endorsementCount + 1 }, index);
                } else {
                    enqueueSnackbar(t("Feed.Panels.UserProfessionalInterest.AddEndorsementFailMessage"), { variant: "error" });
                }
            }).catch(e => setViewError(e))
            .finally(() => setPosting(false));
    };

    ///
    const handleRemoveEndorsement = (item: UserPanelProfessionaInterestDto, index: number) => {
        setPosting(true);
        api.v1.userManagement.deleteTopicEndorment({ topicId: item.topicId, userId })
            .then(result => {
                if (result.data === true) {
                    enqueueSnackbar(t("Feed.Panels.UserProfessionalInterest.RemoveEndorsementSuccessMessage"), { variant: "success" });
                    onChange({ ...item, isEndorsed: false, endorsementCount: item.endorsementCount - 1 }, index);
                } else {
                    enqueueSnackbar(t("Feed.Panels.UserProfessionalInterest.RemoveEndorsementFailMessage"), { variant: "error" });
                }
            }).catch(e => setViewError(e))
            .finally(() => setPosting(false));
    };

    ///
    const handleSeeAll = () => {
        setShowAll(!showAll);
        setLoading(true);
    };

    ///
    const showEndorsers = (item: UserPanelProfessionaInterestDto) => {
        setDialog(
            <ListUsersDrawer
                title={t("Feed.Panels.TopicSpecialistInterest.UserEndorsedDrawerTitle")}
                onClose={() => setDialog(null)}
                onGetData={({ searchText, pageNumber, pageSize }, signal) =>
                    new Promise<UserItemDtoPaginatedList>((resolve, reject) => {
                        api.v1.topicManagement.getEndorserUsers({ topicId: item.topicId, userId, searchText, pageNumber, pageSize }, { signal })
                            .then(result => resolve(result.data))
                            .catch(e => reject(e));
                    })
                }
            />
        );
    };


    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT
    ////////////////////////////////////////////////////////////////////////////

    // Ao carregar os dados
    useEffect(() => {
        async function getData() {
            try {
                onGetData(showAll ? 0 : APP_PAGE_SIZE_SHORT);
            }
            catch (e) {
                setViewError(e);
            }
            finally {
                setLoading(false);
            }
        }

        if (loading) getData();

    }, [loading]);

    ////////////////////////////////////////////////////////////////////////////
    /// Render
    ////////////////////////////////////////////////////////////////////////////

    return viewError
        ? <MprcRequestErrorReport error={viewError} />
        : viewData.items.length > 0
            ? (
                <MprcContainer margin="normal">
                    {dialog}

                    <MprcContainer margin="dense">
                        <Typography variant="h3">{title}</Typography>
                    </MprcContainer>
                    {
                        loading
                            ? <MprcSpinnerIndicator />
                            : viewData.items.map((item, index) =>
                                <Grid key={item.topicId} container alignItems={session.user.userId != userId ? "baseline" : "center"} spacing={0.6} minHeight={"25px"} marginBottom={0.5}>
                                    {session.user.userId != userId &&
                                        <Grid>
                                            <IconButton
                                                size="small"
                                                disabled={posting}
                                                title={item.isEndorsed
                                                    ? t("Feed.Panels.UserProfessionalInterest.RemoveEndorsementButtonTooltip")
                                                    : t("Feed.Panels.UserProfessionalInterest.AddEndorsementButtonTooltip")
                                                }
                                                onClick={() =>
                                                    item.isEndorsed
                                                        ? handleRemoveEndorsement(item, index)
                                                        : handleAddEndorsement(item, index)
                                                }
                                            >
                                                <FontAwesomeIcon
                                                    icon={faThumbsUp}
                                                    fixedWidth
                                                    color={item.isEndorsed ? theme.palette.highlight.main : theme.palette.muted.light}
                                                />
                                            </IconButton>
                                        </Grid>
                                    }
                                    <Grid size="grow" overflow="hidden">
                                        <Typography variant="h6" component="div" noWrap>
                                            <MprcClickable onClick={() => navigate(KnowledgePaths.FeedTopic(item.topicId))}>{item.name}</MprcClickable>
                                            {
                                                item.isSpecialist &&
                                                <MprcText color="success">
                                                    <span>&nbsp;</span>
                                                    <FontAwesomeIcon
                                                        title={t("Feed.Panels.UserProfessionalInterest.IsSpecialistTooltip")}
                                                        icon={faShieldCheck}
                                                    />
                                                </MprcText>
                                            }
                                            {
                                                item.hasProvenKnowledgeSeal && !item.isSpecialist &&
                                                <MprcText color="info">
                                                    <span>&nbsp;</span>
                                                    <FontAwesomeIcon title={t("Feed.Panels.UserProfessionalInterest.VerifiedKnowerTooltip")} icon={faShieldQuartered} />
                                                </MprcText>
                                            }
                                        </Typography>
                                        {
                                            item.endorsementCount > 0 &&
                                            <Typography variant="caption" sx={{ my: .5 }} component="div">
                                                <MprcLink fontSize="inherit" onClick={() => showEndorsers(item)}>
                                                    <b>{t("Feed.Panels.UserProfessionalInterest.PeopleText", { count: item.endorsementCount })}</b>
                                                    &nbsp;
                                                    <Box
                                                        component="span"
                                                        color={theme.palette["muted"].main}
                                                    >
                                                        {t("Feed.Panels.UserProfessionalInterest.EndorssedText", { count: item.endorsementCount })}
                                                    </Box>
                                                </MprcLink>
                                            </Typography>
                                        }
                                    </Grid>
                                </Grid>
                            )
                    }
                    {
                        viewData.totalRows > APP_PAGE_SIZE_SHORT &&
                        <Box marginTop={1}>
                            <Typography
                                variant="linkActionHeader"
                                component="a"
                                onClick={() => handleSeeAll()}>
                                <b>{showAll ? t("Feed.Panels.UserProfessionalInterest.SeeLessText") : t("Feed.Panels.UserProfessionalInterest.SeeAllText")}</b>
                            </Typography>
                        </Box>
                    }
                </MprcContainer>
            )
            : null;
};
